import React, { useState } from 'react';
import { Info, ChevronDown, HelpCircle, Smartphone } from 'lucide-react';

const BASE = `${process.env.PUBLIC_URL || ''}/help`;
const STEPS = [
  { n: 1, title: 'Open Trips → History', text: 'In your Amazon Relay account, go to Trips and open the History tab. Pick the week you want.' },
  { n: 2, title: 'Click "More filters"', text: 'Just under the search bar, tap More filters.', img: `${BASE}/step2-more-filters.jpg` },
  { n: 3, title: 'Status → Completed', text: 'Open the Status box and choose Completed (not Canceled or Rejected).', img: `${BASE}/step3-status-completed.jpg` },
  { n: 4, title: 'Click "Export"', text: 'Click Export in the top-right corner of the results and download the file. Upload that file here.', img: `${BASE}/step4-export.jpg` },
];

/** Reminder + step-by-step guide (with screenshots) for exporting the right Amazon file. */
export const TripImportHelp = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2" data-testid="trip-import-help">
      <div className="flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-900/60 bg-blue-50/70 dark:bg-blue-950/30 px-3 py-2.5 text-xs text-slate-700 dark:text-blue-100" data-testid="trip-import-reminder">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
        <p>
          Upload the <strong>Trip History</strong> export (Trips → History → Export), <strong>not the Payment file</strong>.
          The payment file has no driver information, so loads can't be matched to units.
          You can add payment files separately below to check which loads were paid.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-blue-200 dark:border-blue-900/60 bg-blue-50/60 dark:bg-slate-800/60 px-3 py-2 text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-100/60 transition-colors"
        aria-expanded={open}
        data-testid="trip-import-help-toggle"
      >
        <span className="flex items-center gap-2"><HelpCircle className="h-4 w-4" /> More help: how to export Trip History</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ol className="space-y-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3" data-testid="trip-import-help-steps">
          {STEPS.map((s) => (
            <li key={s.n} className="space-y-1.5" data-testid={`trip-import-help-step-${s.n}`}>
              <div className="flex items-start gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">{s.n}</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{s.title}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{s.text}</p>
                </div>
              </div>
              {s.img && (
                <a href={s.img} target="_blank" rel="noreferrer" className="block ml-8">
                  <img src={s.img} alt={s.title} loading="lazy" className="w-full max-w-xl rounded-md border border-slate-200 dark:border-slate-700" />
                </a>
              )}
            </li>
          ))}
          <li className="flex items-start gap-2 rounded-md bg-slate-50 dark:bg-slate-800/60 p-2 text-xs text-slate-700 dark:text-slate-300">
            <Smartphone className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
            On a phone? Turn it sideways (landscape) in the Relay website so the Export button shows up.
          </li>
        </ol>
      )}
    </div>
  );
};
